import IServiceCoursesProvider from "./ServiceCoursesProvider";
import axios from "axios";
import { AxiosRequestConfig, AxiosResponseHeaders } from "axios";
import { IMsalContext } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { loginRequest } from "AuthConfig";
import { Lang } from 'contexts/LanguageContext';

export class ServiceCoursesApi implements IServiceCoursesProvider {
    
    public async getFeaturedCourses() {
        const apiUrl =  process.env.REACT_APP_API_FEATURED_SESSIONS_URL ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY ?? "";
        try {
            const fCourses= await axios.get(apiUrl, {
                headers: {
                    "Ocp-Apim-Subscription-Key": apiSubscriptionKey
                }
            });
            if (fCourses.status >= 500) {
                throw new Error(`Server error (${fCourses.status}) while fetching featured courses`);
            }
            return (fCourses?.data && fCourses.status === 200) ? fCourses.data.sessions : [];
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while fetching featured courses`);
            }
            console.error(error);
            throw error;
        }
    }

    public async getCoursesByMonth(month: string) {
        const apiUrl = process.env.REACT_APP_API_SESSION_URL ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY ?? "";
        try {
            const coursesByMonth = await axios.get(apiUrl+"?month="+month, {
                headers: {
                    "Ocp-Apim-Subscription-Key": apiSubscriptionKey
                }
            });
            if (coursesByMonth.status >= 500) {
                throw new Error(`Server error (${coursesByMonth.status}) while fetching courses by month`);
            }
            return (coursesByMonth.data && coursesByMonth.status === 200) ? coursesByMonth.data.sessions : [];
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while fetching courses by month`);
            }
            console.error(error);
            throw error;
        }
    }

    public async getCourseById(courseId: string) {
        const apiUrl = process.env.REACT_APP_API_SESSION_URL ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY ?? "";
        try {
            const courseById = await axios.get(apiUrl+"/"+courseId, {
                headers: {
                    "Ocp-Apim-Subscription-Key": apiSubscriptionKey
                }
            });
            if (courseById.status >= 500) {
                throw new Error(`Server error (${courseById.status}) while fetching course by id`);
            }
            return (courseById.data && courseById.status === 200) ? courseById.data : {};
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while fetching course by id`);
            }
            console.error(error);
            throw error;
        }
    }

    public async getRegistrations(msal: IMsalContext, lang: Lang) {
        const apiUrl = process.env.REACT_APP_API_REGISTRATIONS_URL ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY  ?? "";
        const headers = {
            "Ocp-Apim-Subscription-Key": apiSubscriptionKey
        };
        try {
            const axiosConfig = await this.getAuthorizedAxiosConfig(msal, lang, (headers as any));
            const registrations = await axios.get(apiUrl, axiosConfig);
            if (registrations.status >= 500) {
                throw new Error(`Server error (${registrations.status}) while fetching registrations`);
            }
            return (registrations.data && registrations.status === 200) ? registrations.data.inscriptions : [];
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while fetching registrations`);
            }
            console.error(error);
            throw error;
        }
    }

    public async getParticipations(msal: IMsalContext, lang: Lang, sessionId: string) {
        const apiUrl = (process.env.REACT_APP_API_PARTICIPATION_URL) ? process.env.REACT_APP_API_PARTICIPATION_URL.replace("{sessionId}", sessionId) : "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY  ?? "";
        const headers = {
            "Ocp-Apim-Subscription-Key": apiSubscriptionKey
        };
        try {
            const axiosConfig = await this.getAuthorizedAxiosConfig(msal, lang, (headers as any));
            const participations = await axios.get(apiUrl, axiosConfig);
            if (participations.status >= 500) {
                throw new Error(`Server error (${participations.status}) while fetching participations`);
            }
            return (participations.data && participations.status === 200) ? participations.data : {};
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while fetching participations`);
            }
            console.error(error);
            throw error;
        }
    }

    public async getPresences(msal: IMsalContext, lang: Lang) {
        const apiUrl = process.env.REACT_APP_API_PRESENCES_URL ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY  ?? "";
        const headers = {
            "Ocp-Apim-Subscription-Key": apiSubscriptionKey
        };
        try {
            const axiosConfig = await this.getAuthorizedAxiosConfig(msal, lang, (headers as any));
            const presences = await axios.get(apiUrl, axiosConfig);
            if (presences.status >= 500) {
                throw new Error(`Server error (${presences.status}) while fetching presences`);
            }
            return (presences.data && presences.status === 200) ? presences.data.presences : [];
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while fetching presences`);
            }
            console.error(error);
            throw error;
        }
    }

    public async getCoursesCategories() {
        const apiUrl = process.env.REACT_APP_API_CATEGORIES_URL ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY  ?? "";
        try {
            const coursesCategories = await axios.get(apiUrl, {
                headers: {
                    "Ocp-Apim-Subscription-Key": apiSubscriptionKey
                }
            });
            if (coursesCategories.status >= 500) {
                throw new Error(`Server error (${coursesCategories.status}) while fetching course categories`);
            }
            return (coursesCategories.data && coursesCategories.status === 200) ? coursesCategories.data.categoriesFormation : [];
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while fetching course categories`);
            }
            console.error(error);
            throw error;
        }
    }

    public async getTypesSubscription(msal: IMsalContext, lang: Lang) {
        const apiUrl = process.env.REACT_APP_API_SUBSCRIPTION_TYPES ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY ?? "";
        const headers = {
            "Ocp-Apim-Subscription-Key": apiSubscriptionKey
        };
    
        try {
            const axiosConfig = await this.getAuthorizedAxiosConfig(msal, lang, headers as any);
            const typesSubscription = await axios.get(apiUrl, axiosConfig);
            if (typesSubscription.status >= 500) {
                throw new Error(`Server error (${typesSubscription.status}) while fetching subscription types`);
            }
            return (typesSubscription.data && typesSubscription.status === 200)
                ? typesSubscription.data
                : [];
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while fetching subscription types`);
            }
            console.error(error);
            throw error;
        }
    }

    public async sendSubscription(msal: IMsalContext, lang: Lang, dataToSend: any) {
        const apiUrl =  process.env.REACT_APP_API_SUBSCRIPTION_SUBMIT_URL ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY ?? "";
        const headers = {
            "Ocp-Apim-Subscription-Key": apiSubscriptionKey
        };
        try {
            const axiosConfig = await this.getAuthorizedAxiosConfig(msal, lang, (headers as any));
            const newSubscription = await axios.post(apiUrl, dataToSend, axiosConfig);
            if (newSubscription.status >= 500) {
                throw new Error(`Server error (${newSubscription.status}) while sending subscription`);
            }
            return (newSubscription && newSubscription.status === 200) ? newSubscription.data : {error: newSubscription.data};
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while sending subscription`);
            }
            console.error(error);
            throw error;
        }
    }

    public async getSubscriptions(msal: IMsalContext, lang: Lang) {
        const apiUrl = process.env.REACT_APP_API_SUBSCRIPTION_URL ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY ?? "";
        const headers = {
            "Ocp-Apim-Subscription-Key": apiSubscriptionKey
        };
        try {
            const axiosConfig = await this.getAuthorizedAxiosConfig(msal, lang, (headers as any));
            const coursesSubscriptions = await axios.get(apiUrl, axiosConfig);
            if (coursesSubscriptions.status >= 500) {
                throw new Error(`Server error (${coursesSubscriptions.status}) while fetching subscriptions`);
            }
            // If no subscription found, return empty object/array as before
            return (coursesSubscriptions.data && coursesSubscriptions.status === 200) ? coursesSubscriptions.data : [];
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while fetching subscriptions`);
            }
            console.error(error);
            throw error;
        }
    }

    public async createSubscription(msal: IMsalContext, lang: Lang) {
        const apiUrl = process.env.REACT_APP_API_CREATE_SUBSCRIPTION_URL ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY ?? "";
        const headers = {
            "Ocp-Apim-Subscription-Key": apiSubscriptionKey
        };
        try {
            const axiosConfig = await this.getAuthorizedAxiosConfig(msal, lang, (headers as any));
            const language = msal.accounts[0].idTokenClaims?.extension_communicationLangue ?? lang;
            const coursesSubscription = await axios.post(apiUrl, {"langue": language}, axiosConfig);
            if (coursesSubscription.status >= 500) {
                throw new Error(`Server error (${coursesSubscription.status}) while creating subscription`);
            }
            return (coursesSubscription && coursesSubscription.status === 200) ? coursesSubscription.data : null;
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while creating subscription`);
            }
            console.error(error);
            throw error;
        }
    }

    public async updateSubscription(msal: IMsalContext, lang: Lang, invitationCode: string) {
        const apiUrl = process.env.REACT_APP_API_SUBSCRIPTIONS_ROOT_URL ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY  ?? "";
        const headers = {
            "Ocp-Apim-Subscription-Key": apiSubscriptionKey
        };
        try {
            const axiosConfig = await this.getAuthorizedAxiosConfig(msal, lang, (headers as any));
            const updatedSubscription = await axios.put(
                `${apiUrl}${invitationCode}/update`,
                {
                    "name": msal.accounts[0].idTokenClaims?.given_name,
                    "LastName": msal.accounts[0].idTokenClaims?.family_name,
                    "DisplayName": msal.accounts[0].idTokenClaims?.name
                },
                axiosConfig
            );
            if (updatedSubscription.status >= 500) {
                throw new Error(`Server error (${updatedSubscription.status}) while updating subscription`);
            }
            return updatedSubscription;
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while updating subscription`);
            }
            console.error(error);
            throw error;
        }
    }

    public async assistSession(msal: IMsalContext, lang: Lang, sessionId: string, subscriptionId: string) {
        const apiUrl = process.env.REACT_APP_API_SESSIONS_ROOT_URL ?? "";
        const subUrl = process.env.REACT_APP_API_ASSIST_SUB_URL ?? "";
        const apiSubscriptionKey = process.env.REACT_APP_API_SUBSCRIPTION_KEY ?? "";
        const headers = {
            "Ocp-Apim-Subscription-Key": apiSubscriptionKey
        };
        try {
            const axiosConfig = await this.getAuthorizedAxiosConfig(msal, lang, (headers as any));
            const updatedSubscription = await axios.post(
                `${apiUrl}${sessionId}/${subUrl}`,
                {
                    "abonneId": subscriptionId
                },
                axiosConfig
            );
            if (updatedSubscription.status >= 500) {
                throw new Error(`Server error (${updatedSubscription.status}) while assisting session`);
            }
            return updatedSubscription;
        } catch (error: any) {
            if (error.response && error.response.status >= 500) {
                throw new Error(`Server error (${error.response.status}) while assisting session`);
            }
            console.error(error);
            throw error;
        }
    }

    private async getAuthorizedAxiosConfig (
        context: IMsalContext,
        lang: Lang,
        headers?: AxiosResponseHeaders,
        config?: AxiosRequestConfig,
    ): Promise<AxiosRequestConfig> {
        
        let accessToken;
        let scopes: any = [
           process.env.REACT_APP_SCOPE 
            , ...loginRequest.scopes
        ];
        try {
          const response = await context.instance.acquireTokenSilent({
            scopes: scopes,
            forceRefresh: true,
            account: context.accounts[0],
            extraQueryParameters: {
                ui_locales: lang === 'fr' ? "fr-ca" : 'en-ca' // Change this to the desired locale
              },
            redirectUri: "/blank.html"
          });
          accessToken = response.accessToken;
        } catch (error) {
          if (error instanceof InteractionRequiredAuthError) {
            await context.instance.acquireTokenRedirect({
              scopes: scopes,
              account: context.accounts[0],
              extraQueryParameters: {
                ui_locales: lang === 'fr' ? "fr-ca" : 'en-ca' // Change this to the desired locale
              }
            });
          } else {
            // Error was not related to needing interaction
            throw error;
          }
        }
      
        return {
          ...config,
          withCredentials: true,
          validateStatus: () => true,
          headers: {
            ...headers,
            // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
            Authorization: `Bearer ${accessToken}`,
          },
        };
    }
}